'use client'

import TickIcon from '@/components/icons/tick'
import { Button } from '@/components/ui/button'
import { trackClick } from '@/lib/mixpanel'
import apiFetch from '@/store/helpers/api-fetch'
import { Loader2 } from 'lucide-react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import useSWR from 'swr'

/**
 *
 * @param {object} session - User session object
 * @returns {jsx} - Link iRacing account button
 *
 * @example
 * <LinkIRacingButton session={session} /> - Basic usage
 */
export default function LinkIRacingButton({ session, classNameProp }) {
  const pathname = usePathname()
  const sessionUserId = session?.user?.id || null

  if (!sessionUserId) {
    return (
      <Link
        data-tracking-event={'Sign in button clicked'}
        href={`/auth/login?redirectUrl=${pathname}`}
        onClick={trackClick}
        className={classNameProp}
      >
        Login
      </Link>
    )
  }

  // Check if iRacing account already exists for this user
  const {
    data: iRacingConnectedAccount,
    error: _iRacingConnectedAccountError,
    isLoading: iRacingConnectedAccountLoading,
  } = useSWR(
    {
      url: `user/${sessionUserId}/connected-account`,
      searchParams: {
        where: {
          serviceId: 'iracing',
        },
        isSingle: true,
      },
    },
    apiFetch,
  )

  // Display whilst checking connected account
  if (iRacingConnectedAccountLoading) {
    return (
      <Button disabled>
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        Checking if iRacing account is linked...
      </Button>
    )
  }

  // No iRacing account is linked
  if (
    !iRacingConnectedAccount ||
    Object.keys(iRacingConnectedAccount).length === 0
  ) {
    return (
      <Button asChild className="w-full">
        <Link href="/profile-manager/settings/connected-accounts">
          Link iRacing account
        </Link>
      </Button>
    )
  }

  // iRacing account linked
  return (
    <div className="flex items-center justify-center gap-2">
      <TickIcon className="size-6 stroke-success" />
      <p className="text-h-4-sm">iRacing account linked</p>
    </div>
  )
}
